import apiClient from "../lib/apiClients";
import {
  AppealApplication,
  AppealApplicationFeedback,
  AppealApplicationRespondBack,
} from "types";
import { CourtMarks, withCourtMarks } from "components/CourtFilingMarks";

const BASE = "/appeal";

// Build multipart FormData from a plain object — files (and arrays of files) are
// appended with repeated keys; other scalars as-is. Skips meta/nested keys. The
// apiClient default Content-Type is application/json, so multipart requests must
// override the header (axios sets the boundary for a real FormData regardless).
const SKIP_KEYS = [
  "id",
  "documents",
  "feedbacks",
  "respond_backs",
  "created_at",
  "updated_at",
];

const buildFormData = (data: Record<string, any>): FormData => {
  const fd = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (SKIP_KEYS.includes(key)) return;
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item instanceof File) fd.append(key, item);
      });
      return;
    }
    if (value instanceof File) {
      fd.append(key, value);
      return;
    }
    if (typeof value === "object") return;
    fd.append(key, String(value));
  });
  return fd;
};

const multipart = { headers: { "Content-Type": "multipart/form-data" } };

const buildQuery = (params?: Record<string, any>) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.append(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
};

export interface AppealApplicationListParams {
  appeal_request_id?: string;
  defendant_appeal_request_id?: string;
  case_id?: string;
  status?: string;
  assigned_prosecutor_id?: string;
}

export const fetchAppealApplications = async (
  params?: AppealApplicationListParams,
) => {
  const response = await apiClient.get<AppealApplication[]>(
    `${BASE}/appeal-applications/${buildQuery(params)}`,
  );
  return response.data;
};

export const fetchAppealApplicationById = async (id: string) => {
  const response = await apiClient.get<AppealApplication>(
    `${BASE}/appeal-applications/${id}/`,
  );
  return response.data;
};

// Create (multipart): appeal_request OR defendant_appeal_request, title,
// application_summary, files under application_file.
export const addAppealApplication = async (data: AppealApplication) => {
  const response = await apiClient.post(
    `${BASE}/appeal-applications/`,
    buildFormData(data as Record<string, any>),
    multipart,
  );
  return response.data;
};

// File/content edit (multipart) — e.g. revising the application or attaching the
// court report file.
export const editAppealApplication = async (data: AppealApplication) => {
  const response = await apiClient.patch(
    `${BASE}/appeal-applications/${data?.id}/`,
    buildFormData(data as Record<string, any>),
    multipart,
  );
  return response.data;
};

// Workflow advance / simple field PATCH (JSON).
export const updateAppealApplication = async (
  data: Partial<AppealApplication>,
  marks?: CourtMarks,
) => {
  const { body, config } = withCourtMarks(data, marks);
  const response = await apiClient.patch(
    `${BASE}/appeal-applications/${data?.id}/`,
    body,
    config,
  );
  return response.data;
};

export const addAppealApplicationFeedback = async (
  data: AppealApplicationFeedback,
) => {
  const response = await apiClient.post(
    `${BASE}/appeal-application-feedbacks/`,
    data,
  );
  return response.data;
};

export const addAppealApplicationRespondBack = async (
  data: AppealApplicationRespondBack,
) => {
  const response = await apiClient.post(
    `${BASE}/appeal-application-respond-backs/`,
    data,
  );
  return response.data;
};
